import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { elowenClient, ElowenApiError } from './elowenClient';
import { useTranslation } from './i18n';
import type { Locale } from './i18n/dictionaries';
import { updateStatusActive } from './updateStatus';
import type { SystemUpdateStatus } from './types';
import { parseSkinCatalogue, type SkinCatalogue } from './skins';
import { z } from 'zod';
import type { InstantRange } from 'elowen-plugin-ui-kit';
import { RESOURCE_HISTORY, RESOURCE_POINT_STATES, RESOURCE_SERIES_IDS, RESOURCE_SERIES_UNITS, validResourceHistory } from './resourceHistoryChart';
// Type-only, so it is erased before the bundler ever resolves it: the app takes the vocabulary's types
// from the bundle contract and its behaviour from the generated copy of that bundle (lib/resourceHistoryChart.js).
import type { ResourceHistoryMetric } from 'elowen-plugin-ui-kit/resourceHistory';
import type { HostCapacityResource, HostCpuResource, HostIoResource, HostNetworkResource, HostResourceSnapshot, ResourceHistoryPoint, ResourceHistoryResponse } from '../../src/shared/wireContract';
import { instantRangeBounds } from './dateRange';
import type { SkinAdminView, ProviderUsageStats, ConversationLinksResponse, DashRecap, MemoryFilters, SlashCommandDef, UsageOriginGroup, UsageScope, ApiTokenListItem, ApiTokenStats, CliDeviceCodeView, CliDeviceStats } from './types';

export const QUERY_KEYS = {
  health: ['health'] as const,
  config: ['config'] as const,
  voiceCatalog: ['voice-catalog'] as const,
  me: ['me'] as const,
  /** An account's signed avatar link, keyed `[...avatarUrl, userId]` and shared by every `Avatar` of that
   *  account on screen. The upload invalidates the uploader's own entry so the new picture shows at once. */
  avatarUrl: ['avatar-url'] as const,
  accountSecurity: ['account-security'] as const,
  loginSessions: ['login-sessions'] as const,
  system: ['system'] as const,
  /** The host's live resource readings, read on their own cadence by the System settings rings. */
  hostMetrics: ['system-metrics'] as const,
  systemUpdate: ['system-update'] as const,
  environmentOperations: ['environment-operations'] as const,
  systemReadiness: ['system-readiness'] as const,
  usageByModel: ['usage-by-model'] as const,
  usageByDay: ['usage-by-day'] as const,
  usageByOrigin: ['usage-by-origin'] as const,
  usageByProvider: ['usage-by-provider'] as const,
  memories: ['memories'] as const,
  /** The caller's bell alerts. Freshness is SSE-driven: every raise/clear publishes a content-free
   *  per-recipient `alert` event and `useElowenEvents` invalidates this key, so no poll is needed. */
  alerts: ['alerts'] as const,
  embeddingSettings: ['embedding-settings'] as const,
  memoryCategories: ['memory-categories'] as const,
  categorizationSettings: ['categorization-settings'] as const,
  memoryMaintenance: ['memory-maintenance'] as const,
  /** The caller's own automatic memory-maintenance passes. The run-id cursor of the page being read is
   *  the rest of the key; the SSE `memory` event invalidates this prefix, so a finished pass refreshes
   *  every page a reader has already loaded. */
  memoryDreaming: ['memory-dreaming'] as const,
  /** The account's `memoryDreaming` preference blob. Kept apart from the status above because only this
   *  one carries the revision its PATCH is checked against. */
  myMemoryDreaming: ['my-memory-dreaming'] as const,
  brainCommands: ['brain-commands'] as const,
  brainRateLimits: ['brain-rate-limits'] as const,
  brainContextUsage: ['brain-context-usage'] as const,
  brainDebugSessions: ['brain-debug-sessions'] as const,
  brainDebugRequests: ['brain-debug-requests'] as const,
  brainDebugRequest: ['brain-debug-request'] as const,
  /** The prefix; the scope (`mine` / `all`) is the second element, so invalidating the prefix refreshes
   *  both the sidebar's and the register's branches at once. */
  brainConversationLinks: ['brain-conversation-links'] as const,
  pluginUi: ['plugin-ui'] as const,
  platformMetadata: ['platform-metadata'] as const,
  userPluginConfigs: ['user-plugin-configs'] as const,
  vault: ['vault'] as const,
  vaultShares: ['vault-shares'] as const,
  /** Followed by the item name and the day window. */
  vaultStats: ['vault-stats'] as const,
  apiTokens: ['api-tokens'] as const,
  /** The instance's skins as the ADMIN surface sees them: the served catalogue plus the folders that
   *  were refused. This one exists for the Settings rows; the served catalogue itself is `skinCatalogue`. */
  skinAdmin: ['skin-admin'] as const,
  /** The PUBLIC catalogue every document resolves against — the same listing the server fetched for the
   *  first paint (lib/skinServer.ts) and seeded this query with. */
  skinCatalogue: ['skin-catalogue'] as const,
  /** Followed by the token id and the day window. */
  apiTokenStats: ['api-token-stats'] as const,
  /** ADMIN: the instance-wide account defaults, with each field's own-value count and revision. */
  usersDefaults: ['users-defaults'] as const,
  accountPresets: ['account-presets'] as const,
  /** ADMIN: the default per-account form of every plugin that declares one. */
  usersDefaultsPlugins: ['users-defaults-plugins'] as const,
  cliEnrollment: ['cli-enrollment'] as const,
  /** Followed by the target id and the day window. */
  cliDeviceStats: ['cli-device-stats'] as const,
  /** Followed by the user code. */
  cliDeviceCode: ['cli-device-code'] as const,
};

/** Server-owned enrollment lock, sampled against a monotonic browser clock to avoid wall-clock skew.
 *  Re-read on every mount and every 10 s so redemption and daemon restarts release the lock. */
export const useCliEnrollment = (enabled: boolean) =>
  useQuery({
    queryKey: QUERY_KEYS.cliEnrollment,
    enabled,
    queryFn: async () => {
      const { enrollment } = await elowenClient.cliEnrollment();
      return {
        enrollment,
        deadline: enrollment === null ? 0 : performance.now() + enrollment.remainingMs,
        availableAt: enrollment === null ? 0 : Date.now() + enrollment.remainingMs,
      };
    },
    refetchOnMount: 'always',
    refetchInterval: 10_000,
  });

/** One paired computer's counters over inclusive UTC days, read while its row is expanded or its drawer's
 *  activity section is on screen. */
export const useCliDeviceStats = (targetId: string, from: string, to: string, enabled: boolean) =>
  useQuery<CliDeviceStats>({
    queryKey: [...QUERY_KEYS.cliDeviceStats, targetId, from, to],
    queryFn: () => elowenClient.cliDeviceStats(targetId, from, to),
    enabled,
  });

/** A pending `elowen login`, by the code the link carries. */
export const useCliDeviceCode = (userCode: string) =>
  useQuery<CliDeviceCodeView>({
    queryKey: [...QUERY_KEYS.cliDeviceCode, userCode],
    queryFn: () => elowenClient.cliDeviceCode(userCode),
  });

/** Only vault metadata enters the host cache; secret writes stay in the open form. */
export const useVaultItems = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.vault, queryFn: () => elowenClient.vaultItems(), enabled, retry: false });

/** Owner-only share metadata, cached per item and fetched only when needed. */
export const useVaultShares = (name: string, enabled: boolean) =>
  useQuery({ queryKey: [...QUERY_KEYS.vaultShares, name, 'shares'], queryFn: () => elowenClient.vaultShares(name), enabled, retry: false });
export const useVaultShareCandidates = (name: string, enabled: boolean) =>
  useQuery({ queryKey: [...QUERY_KEYS.vaultShares, name, 'share-candidates'], queryFn: () => elowenClient.vaultShareCandidates(name), enabled, retry: false });

/** One vault item's uses over inclusive UTC days, read only while its card item is expanded. */
export const useVaultItemStats = (name: string, from: string, to: string, enabled: boolean) =>
  useQuery({
    queryKey: [...QUERY_KEYS.vaultStats, name, from, to],
    queryFn: () => elowenClient.vaultItemStats(name, from, to),
    enabled,
  });

export const useApiTokens = () =>
  useQuery<{ tokens: ApiTokenListItem[] }>({
    queryKey: QUERY_KEYS.apiTokens,
    queryFn: () => elowenClient.apiTokens(),
  });

/** One token's counters over inclusive UTC days, read only while its card item is expanded. */
export const useApiTokenStats = (id: number, from: string, to: string, enabled: boolean) =>
  useQuery<ApiTokenStats>({
    queryKey: [...QUERY_KEYS.apiTokenStats, id, from, to],
    queryFn: () => elowenClient.apiTokenStats(id, from, to),
    enabled,
  });

/** The published slash-command menu for the web surface — the single source of truth is the daemon's
 *  live plugin registry (GET /brain/commands). Held in the query cache (not a one-shot fetch) so a plugin
 *  toggle / config change / install can invalidate it and the menu re-pulls, instead of showing a stale
 *  snapshot captured when the chat first mounted. Empty when the brain is unwired. */
export const useBrainCommands = () =>
  useQuery<SlashCommandDef[]>({
    queryKey: QUERY_KEYS.brainCommands,
    queryFn: () => elowenClient.brainCommands().then((r) => r.commands),
    staleTime: 60_000,
  });

/** The branches organized under each conversation — the collapsed "Scheduled jobs" and "Sub-agents"
 *  groups in the chat sidebar (`mine`) and in the admin register (`all`).
 *
 *  ONE request per list, holding the whole minimal summary array: search has to see every link before the
 *  register pages its roots, and a request per visible row would multiply a quiet navigation aid into a
 *  burst. There is no poll — schedules change when somebody edits them, and the cron mutations invalidate
 *  this key, as does a sub-agent starting or a sub-agent or workflow finishing — but a focus refetch is kept (against the client
 *  default) so a tab left open beside the cron editor catches up on return instead of listing a job that
 *  was renamed elsewhere.
 *
 *  `conversationIds` narrows the SUB-AGENT half to the page of roots on screen. A tree read is far more
 *  expensive than a job list, and an instance with hundreds of conversations should not walk all of them
 *  to draw twenty rows. The jobs half is unaffected and still spans the whole authorized listing. */
export const useConversationJobLinks = (scope: 'mine' | 'all' = 'mine', conversationIds?: readonly string[], enabled = true) => {
  // The ids join the key NORMALIZED — sorted, then joined — so paging back to a page already fetched hits
  // the cache instead of re-asking, and re-rendering one page never splits it into two cache entries.
  const ids = conversationIds ? [...conversationIds].sort().join(',') : null;
  // Keep observing an in-flight page when filtering removes every root. Only request identity is
  // retained here; its response stays in QueryClient, and a scope change discards the retained page.
  const lastRequest = useRef({ scope, ids, conversationIds });
  if (lastRequest.current.scope !== scope || ids !== '') {
    lastRequest.current = { scope, ids, conversationIds: conversationIds?.slice() };
  }
  const request = lastRequest.current;
  return useQuery<ConversationLinksResponse>({
    queryKey: [...QUERY_KEYS.brainConversationLinks, scope, request.ids],
    queryFn: () => elowenClient.brainConversationLinks(scope, request.conversationIds),
    // Cold search must discover schedules even before any root matches. Explicit empty ids ask for
    // schedules only; after a page exists, retain its request without starting another empty read.
    enabled: enabled && (ids !== '' || request.ids === ''),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Preserve page changes, never data from a different authorization scope.
    placeholderData: (previous, previousQuery) => previousQuery?.queryKey[1] === scope ? previous : undefined,
  });
};

/** A finished workflow node's complete result for the inspector's detail panel. A settled attempt's
 *  result never changes, so it never goes stale; the key carries the attempt instead. WorkflowResume
 *  retries a node under the same workflow id (and often the same child session) but gives the new
 *  attempt a fresh `startedAt`, so a retried node is fetched again rather than showing the previous
 *  attempt's error. */
export const useWorkflowNodeResult = (
  workflowId: string,
  node: { id: string; status: string; startedAt?: number },
) =>
  useQuery({
    queryKey: ['workflow-node-result', workflowId, node.id, node.startedAt ?? null, node.status],
    queryFn: () => elowenClient.brainWorkflowNodeResult(workflowId, node.id),
    enabled: node.status === 'done' || node.status === 'error',
    staleTime: Infinity,
  });

/** Total token/cost usage aggregated per model, for the stats page and the dashboard's monthly usage
 *  card. Cost/tokens move slowly. `window` (finite bounds only go into the key — an open `±Infinity`
 *  bound collapses to `null` so every rolling/all-time preset shares one cache entry). The scope sits
 *  under the shared key prefix, so a usage reset invalidates the personal and instance views alike. */
export const useModelUsage = (window?: { fromMs: number; toMs: number }, scope: UsageScope = 'personal') =>
  useQuery({
    queryKey: [...QUERY_KEYS.usageByModel, scope,
      Number.isFinite(window?.fromMs) ? window!.fromMs : null,
      Number.isFinite(window?.toMs) ? window!.toMs : null],
    queryFn: () => elowenClient.usageByModel(window, scope),
    refetchInterval: 30_000,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });

/** Daily spend over the last `days` days, for the dashboard's spend sparkline. Slow-moving, so a
 *  gentle 60 s poll. Only days with settled tasks come back — the tile pads the missing days. */
export const useUsageByDay = (days = 7, scope: UsageScope = 'personal') =>
  useQuery({
    queryKey: [...QUERY_KEYS.usageByDay, scope, days],
    queryFn: () => elowenClient.usageByDay(days, scope),
    refetchInterval: 60_000,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });

/** ADMIN-ONLY: who spent the tokens, and from which address. `enabled` exists because the route answers
 *  403 to a non-admin BY DESIGN — the caller passes the admin flag it already has so a normal account
 *  never fires a request that is meant to fail. The server decides regardless; this only avoids noise.
 *  Polled gently: the drawer is opened deliberately and the rollup moves at the pace of settled turns. */
export const useUsageByOrigin = (
  group: UsageOriginGroup = 'pair',
  window?: { fromMs: number; toMs: number },
  opts?: { enabled?: boolean; limit?: number },
) =>
  useQuery({
    queryKey: [...QUERY_KEYS.usageByOrigin, group,
      Number.isFinite(window?.fromMs) ? window!.fromMs : null,
      Number.isFinite(window?.toMs) ? window!.toMs : null,
      opts?.limit ?? 50],
    queryFn: () => elowenClient.usageByOrigin(group, window, opts?.limit ?? 50),
    enabled: opts?.enabled !== false,
    refetchInterval: 60_000,
  });

/** One provider's statistics together with the UTC-day window they answer for. The window travels with the
 *  data because the previous range's data stays on screen while the next one loads (`placeholderData`): a
 *  caller that drew days from the range it just picked would draw the old figures against the new axis. */
export interface ProviderUsageAnswer { stats: ProviderUsageStats; window: { from: string; to: string } }

/** ADMIN-ONLY: one provider's usage over UTC days, for the statistics drawer in Settings → Elowen AI. Mounted
 *  only while the drawer is open, so nothing polls for a provider nobody is looking at; the previous range
 *  stays on screen while the next one loads (`isPlaceholderData` says so), instead of the drawer collapsing
 *  to a skeleton on every pick. */
export const useProviderUsageStats = (provider: string, window: { from: string; to: string }, limit = 10) =>
  useQuery({
    queryKey: [...QUERY_KEYS.usageByProvider, provider, window.from, window.to, limit],
    queryFn: async (): Promise<ProviderUsageAnswer> => ({ stats: await elowenClient.usageByProvider(provider, window, limit), window }),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });

export const useHealth = () =>
  useQuery({
    queryKey: QUERY_KEYS.health,
    queryFn: elowenClient.health,
    refetchInterval: 10000,
  });

export const useConfig = () =>
  useQuery({ queryKey: QUERY_KEYS.config, queryFn: elowenClient.getConfig });

/** Elowen's version + update posture for the System settings panel. Polled so an "update available"
 *  badge appears without a reload, and so the version flips after a manual/auto update + restart. */
export const useSystem = () =>
  useQuery({ queryKey: QUERY_KEYS.system, queryFn: elowenClient.system, refetchInterval: 60000 });

const hostInstant = z.iso.datetime({ offset: true });
/** A measured amount that a bucket may legitimately have none of. */
const hostAmount = z.nullable(z.number().min(0));
const hostCount = z.number().int().min(0);
const hostResourceState = z.enum(['ready', 'unavailable']);

const hostCpuResourceSchema = z.object({
  state: hostResourceState, usedCpus: z.number().optional(), model: z.string().optional(), percent: z.number().optional(),
}) satisfies z.ZodType<HostCpuResource>;
const hostCapacityResourceSchema = z.object({
  state: hostResourceState, usedBytes: z.number().optional(), limitBytes: z.number().optional(), percent: z.number().optional(),
}) satisfies z.ZodType<HostCapacityResource>;
const hostIoResourceSchema = z.object({
  state: hostResourceState, readBytesPerSec: z.number().optional(), writeBytesPerSec: z.number().optional(),
  limitBytesPerSec: z.number().nullable().optional(), percent: z.number().nullable().optional(),
}) satisfies z.ZodType<HostIoResource>;
const hostNetworkResourceSchema = z.object({
  state: hostResourceState, rxBytesPerSec: z.number().optional(), txBytesPerSec: z.number().optional(),
  limitBytesPerSec: z.number().nullable().optional(), percent: z.number().nullable().optional(),
}) satisfies z.ZodType<HostNetworkResource>;

/** The live host snapshot, validated once at the browser boundary — the daemon's answer is a wire shape,
 *  not a promise, and a field it stops sending must fail loudly here rather than draw as a zero. A resource
 *  the host cannot read is `unavailable` with its figures ABSENT, which is what the rings draw as a mark. */
const hostResourceSnapshotSchema = z.object({
  sampledAt: hostInstant, serverNow: hostInstant, samplingSeconds: z.number(),
  resources: z.object({
    cpu: hostCpuResourceSchema, memory: hostCapacityResourceSchema, disk: hostCapacityResourceSchema,
    io: hostIoResourceSchema, network: hostNetworkResourceSchema,
  }),
}) satisfies z.ZodType<HostResourceSnapshot>;

const hostHistoryPointSchema = z.object({
  at: hostInstant, until: hostInstant, mean: hostAmount, min: hostAmount, max: hostAmount,
  percentMean: hostAmount, percentMax: hostAmount, lastLimit: hostAmount,
  validSamples: hostCount, expectedSamples: hostCount, coverage: z.number().min(0).max(1),
  observedAt: z.nullable(hostInstant), state: z.enum(RESOURCE_POINT_STATES),
  generation: z.nullable(hostCount), discontinuous: z.boolean(),
}) satisfies z.ZodType<ResourceHistoryPoint>;

const hostResourceHistorySchema = z.object({
  range: z.object({ requestedFrom: hostInstant, requestedTo: hostInstant, from: hostInstant, to: hostInstant, adjusted: z.boolean() }),
  serverNow: hostInstant, retentionDays: z.literal(RESOURCE_HISTORY.RETENTION_DAYS), detailedRetentionHours: z.literal(RESOURCE_HISTORY.DETAIL_HOURS),
  resolutionSeconds: z.literal(Object.values(RESOURCE_HISTORY.RESOLUTION_SECONDS)),
  samplingSeconds: z.number(), sampledSince: z.nullable(hostInstant),
  series: z.array(z.object({
    id: z.enum(RESOURCE_SERIES_IDS), unit: z.enum(RESOURCE_SERIES_UNITS),
    points: z.array(hostHistoryPointSchema).max(RESOURCE_HISTORY.MAX_BUCKETS),
  })).max(RESOURCE_SERIES_IDS.length),
}) satisfies z.ZodType<ResourceHistoryResponse>;

/** The host's live readings, on the same cadence as a Project register: the sampler writes one sample a
 *  minute, so a 30 s poll reads the daemon rather than re-measuring the host, and a hidden tab stops
 *  asking. This is deliberately NOT read from the `diagnostics` block of `useSystem`: that one holds a
 *  single shared previous snapshot for its percentage deltas, so a second reader would corrupt it. */
export const useHostMetrics = () =>
  useQuery<HostResourceSnapshot>({
    queryKey: QUERY_KEYS.hostMetrics,
    queryFn: async () => hostResourceSnapshotSchema.parse(await elowenClient.systemMetrics()),
    staleTime: 25_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

/** One range of stored resource history for a host or computer. The daemon owns both
 *  bounds — a preset window is resolved on the server and the effective range comes back in the answer —
 *  so only the preset travels; a custom range is validated here before it is asked for. */
export function useResourceHistory({ subject, range, metrics }: { subject: 'host' | number; range: InstantRange; metrics: readonly ResourceHistoryMetric[] }) {
  const bounds = range.preset === 'custom' ? instantRangeBounds(range) : null;
  const rangeReady = range.preset === 'custom' ? bounds?.valid === true : true;
  const metricIds = [...metrics].sort().join(',');
  const query = useQuery<ResourceHistoryResponse>({
    queryKey: ['resource-history', subject,
      ...(range.preset === 'custom' ? ['custom', range.from, range.to] : ['window', range.preset]), 'auto', metricIds],
    enabled: rangeReady,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({ resolution: 'auto', metrics: metricIds });
      if (range.preset === 'custom') {
        if (!bounds?.valid) throw new Error('invalid_history_range');
        params.set('from', bounds.from);
        params.set('to', bounds.to);
      } else params.set('window', range.preset);
      const parsed = hostResourceHistorySchema.safeParse(await elowenClient.resourceHistory(subject, params, signal));
      if (!parsed.success) throw new Error('invalid_history_response');
      const data = parsed.data;
      if (!validResourceHistory(data, metrics)) throw new Error('invalid_history_response');
      return data;
    },
    // A range that has just been read does not move; the next one is a new key and reads on its own.
    staleTime: 60_000,
    gcTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return {
    data: query.data,
    loading: !query.data && !query.isError,
    error: query.error,
    // The bounds the picker may offer: the retention the host actually keeps, and no future time.
    min: new Date(Date.now() - RESOURCE_HISTORY.RETENTION).toISOString(),
    max: new Date().toISOString(),
    retry: () => { void query.refetch(); },
  };
}

/** One shared status read for all plugin rows and the System panel. Poll only while work is active.
 *  A fresh report refreshes loaded-plugin views, never the initial 202 acceptance. */
export function useSystemUpdateStatus(enabled = true) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: QUERY_KEYS.systemUpdate,
    enabled,
    queryFn: async () => {
      const status = await elowenClient.systemUpdateStatus();
      const previous = qc.getQueryData<SystemUpdateStatus>(QUERY_KEYS.systemUpdate);
      if (status.report?.checked && status.report.at !== previous?.report?.at) {
        for (const queryKey of [['plugins'], ['plugin'], ['marketplace'], QUERY_KEYS.system,
          QUERY_KEYS.config, QUERY_KEYS.brainCommands, QUERY_KEYS.pluginUi, QUERY_KEYS.platformMetadata, QUERY_KEYS.brainConversationLinks]) {
          void qc.invalidateQueries({ queryKey });
        }
      }
      return status;
    },
    staleTime: 1000,
    refetchOnWindowFocus: true,
    refetchInterval: query => updateStatusActive(query.state.data) ? 1000 : false,
  });
}

/** The instance account directory. Admin-only on the daemon, so a caller that renders for non-admins
 *  too passes `enabled: false` for them rather than firing a request that can only come back 403. */
export const useUsers = (enabled = true) => useQuery({ queryKey: ['users'], queryFn: elowenClient.listUsers, enabled });

/** How long the dashboard keeps polling a 'generating' digest before settling for the deterministic
 *  layer: 12 polls × 5 s ≈ one minute — far past a healthy cheap-model generation, short enough that
 *  a permanently failing generator never polls forever. */
const DASH_RECAP_POLL_MS = 5_000;
const DASH_RECAP_POLL_LIMIT = 12;

/** The personalized dashboard recap. Polls only while the daily digest is being generated (bounded),
 *  then holds still — the strip upgrades in place when the digest lands.
 *
 *  The query refreshes with `POST /dash/recap`, which may start the day's digest. `seed` is the /dash or
 *  /chat route's server prefetch, a pure `GET` that never starts one. With it the hero renders the agent-written
 *  greeting on its FIRST frame; without it the query starts empty, the fallback greeting paints, and the
 *  real one replaces it a round-trip later. A seed counts as fresh from now (the server just fetched it),
 *  so the 60s staleTime keeps the client from immediately re-asking for what the document already
 *  carries, unless the seed says a refresh is due: then it is stale from the start and the mount refresh
 *  starts the generation the GET left alone. */
export const useDashRecap = (seed?: DashRecap | null) => {
  const polls = useRef(0);
  return useQuery({
    queryKey: ['dash-recap'],
    queryFn: async () => {
      const data = await elowenClient.dashRecap();
      if (data.digest?.status !== 'generating') polls.current = 0;
      return data;
    },
    ...(seed ? { initialData: seed, ...(seed.refreshDue ? { initialDataUpdatedAt: 0 } : {}) } : {}),
    staleTime: 60_000,
    refetchInterval: (query) => {
      if (query.state.data?.digest?.status !== 'generating') return false;
      polls.current += 1;
      return polls.current <= DASH_RECAP_POLL_LIMIT ? DASH_RECAP_POLL_MS : false;
    },
  });
};

/** The team-feed presence line. Invalidated by the same SSE 'activity' event as the feed. */
export const usePresence = () =>
  useQuery({ queryKey: ['activity-presence'], queryFn: elowenClient.activityPresence });

/** The team pulse tile. Invalidated by the same SSE 'activity' event as the feed, so somebody starting
 *  a turn lights up their layer without a poll. */
export const usePulse = () =>
  useQuery({ queryKey: ['activity-pulse'], queryFn: elowenClient.activityPulse });

export const useActivity = (type?: string, limit?: number) =>
  // SSE activity events invalidate this key; no polling is needed. `limit` joins
  // the key so a small dashboard tail and the full timeline never share one cached payload.
  useQuery({
    queryKey: ['activity', type ?? 'all', limit ?? null],
    queryFn: () => elowenClient.activity({
      ...(type ? { type } : {}),
      ...(limit ? { limit } : {}),
    }),
  });

/** How often a list whose liveness a computer decides is re-read. A computer's `elowen connect` socket
 *  coming up or going down is caused by nothing on this side, and the daemon publishes no event for it, so
 *  the register and the execution targets are re-read while a computer is listed rather than waiting for a
 *  focus or a navigation. */
const COMPUTER_LIVENESS_MS = 10_000;

export const useProjects = (view?: 'assignable') =>
  useQuery({
    queryKey: view === 'assignable' ? ['projects', 'assignable'] : ['projects'],
    queryFn: () => elowenClient.projects(view),
    staleTime: 60_000,
    // A Project being deleted is polled tightly; a listed computer keeps both its state and its readings
    // current on the slower interval.
    refetchInterval: (query) => query.state.data?.some((project) => project.lifecycle === 'deleting') ? 3000
      : query.state.data?.some((project) => project.computer !== undefined) ? COMPUTER_LIVENESS_MS
        : false,
  });

export const useBrainExecutionTargets = () =>
  useQuery({
    queryKey: ['projects', 'execution-targets'],
    queryFn: elowenClient.brainExecutionTargets,
    staleTime: 60_000,
    refetchInterval: (query) => query.state.data?.some((project) => project.computer !== undefined) ? COMPUTER_LIVENESS_MS : false,
  });

export const useProjectSummaries = () =>
  useQuery({ queryKey: ['project-summaries'], queryFn: elowenClient.projectSummaries, staleTime: 30_000 });

export const useProjectGit = (id: number | null, enabled = true) =>
  useQuery({ queryKey: ['project-git', id], queryFn: () => elowenClient.projectGit(id as number), enabled: !!id && enabled });

/** A project's commit log in ANCHORED pages, for a surface that expands a history in place. `anchor` is
 *  the commit the WHOLE walk starts from and the caller names it: every page, including a first page
 *  re-read after a failure, is asked for from that same commit, so no reload shifts the window onto a
 *  newer history and no two pages show the same commit. `fetchNextPage` appends the page after the
 *  loaded ones; `hasNextPage` is false once the log ends. `null` lets the daemon anchor at HEAD, which
 *  is what a caller with no snapshot to name the head from gets. */
export const useProjectCommits = (id: number | null, options: { limit?: number; anchor: string | null; enabled?: boolean }) => {
  const limit = options.limit ?? 50;
  return useInfiniteQuery({
    queryKey: ['project-commits', id, limit, options.anchor],
    queryFn: ({ pageParam }) => elowenClient.projectCommits(id as number, { limit, skip: pageParam.skip, anchor: pageParam.anchor }),
    initialPageParam: { skip: 0, anchor: options.anchor },
    // The skip is the number of commits the loaded pages actually carry, never a counter kept beside
    // them, and it is applied to the anchor rather than to a HEAD that may have moved since.
    getNextPageParam: (page, pages) => page.hasMore
      ? { skip: pages.reduce((total, loaded) => total + loaded.commits.length, 0), anchor: page.anchor }
      : undefined,
    enabled: !!id && options.enabled !== false,
  });
};

export const useProjectUsers = (id: number | null, enabled = true) =>
  useQuery({ queryKey: ['project-users', id], queryFn: () => elowenClient.projectUsers(id as number), enabled: !!id && enabled });

/** The same membership, read with identities. Its own cache key, because it is a different shape of the
 *  same answer — but the assignment mutation invalidates BOTH, so a surface never shows a stale list. */
export const useProjectMemberProfiles = (id: number | null, enabled = true) =>
  useQuery({ queryKey: ['project-member-profiles', id], queryFn: () => elowenClient.projectMemberProfiles(id as number), enabled: !!id && enabled });

/** The project's shared-memory share list (admin-only). Empty = every project member shares the pool. */
export const useProjectMemoryMembers = (id: number | null, enabled = true) =>
  useQuery({ queryKey: ['project-memory-members', id], queryFn: () => elowenClient.projectMemoryMembers(id as number), enabled: !!id && enabled });

/** A managed project's environment state, from the Sandbox plugin's read-only overview. Callers use it
 *  to decide whether the guest filesystem can be asked for anything at all: the file routes provision a
 *  missing environment as a side effect of being called, so a screen that only wants to LOOK has to
 *  learn the state from a route that cannot start one. */
export const useProjectEnvironmentState = (id: number | null) =>
  useQuery({ queryKey: ['project-environment-state', id], queryFn: () => elowenClient.projectEnvironmentState(id as number), enabled: !!id });

export const useAccountSecurity = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.accountSecurity, queryFn: elowenClient.accountSecurity, enabled });

export const useLoginSessions = (enabled = true) =>
  useInfiniteQuery({
    queryKey: QUERY_KEYS.loginSessions,
    queryFn: ({ pageParam }) => elowenClient.loginSessions(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });

export const useMe = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.me, queryFn: elowenClient.me, staleTime: 5 * 60 * 1000, enabled });

/** Enabled plugins with a browser UI — drives the shell nav + the /p/[plugin] host page. Keyed per
 *  locale (labels are localized server-side); held in the query cache so a plugin toggle can invalidate
 *  it and the menu updates without a reload. The root layout seeds the first-paint locale's listing
 *  into this cache. An uncached locale stays pending: labels and view strings from another language
 *  must not be exposed while its own listing loads. */
export const usePluginUi = (locale: string, target?: { plugin: string; page: string }, enabled = true) =>
  useQuery({
    queryKey: target ? [...QUERY_KEYS.pluginUi, locale, target.plugin, target.page] : [...QUERY_KEYS.pluginUi, locale],
    queryFn: () => elowenClient.pluginUi(locale, target),
    enabled,
    staleTime: 60_000,
  });

/** Safe installed platform labels and identity fields, shared per locale without loading plugin UI. */
export const usePlatformMetadata = (locale: Locale) =>
  useQuery({
    queryKey: [...QUERY_KEYS.platformMetadata, locale],
    queryFn: () => elowenClient.platformMetadata(locale),
    staleTime: 60_000,
  });

/** Whether a named plugin contributes a browser UI on this instance — read from the SAME /plugins/ui
 *  listing the sidebar nav uses, so every affordance a plugin owns gates on one source. False while the
 *  listing loads: a plugin's affordances appear only once it is confirmed, so a plugin-less instance
 *  never flashes them. */
export const usePluginPresent = (name: string): boolean => {
  const { locale } = useTranslation();
  const pluginUi = usePluginUi(locale);
  return (pluginUi.data ?? []).some((p) => p.name === name);
};

/** First-run subsystem readiness (admin-only endpoint). Gated by `enabled` so non-admin surfaces never
 *  fire the 403 request. Powers the dashboard "finish setup" nudge and the onboarding checklist. */
export const useSystemReadiness = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.systemReadiness, queryFn: elowenClient.systemReadiness, staleTime: 60_000, enabled });

/** The current user's CLI/brain settings (model override + auto-compact). Edited in Account → CLI. */
export const useMyCliSettings = () =>
  useQuery({ queryKey: ['my-cli-settings'], queryFn: elowenClient.myCliSettings });

/** The current user's web-terminal appearance settings (palette/font/cursor). Edited in Account →
 *  Terminal, and rendered by the preview there. */
export const useMyTerminalSettings = () =>
  useQuery({ queryKey: ['my-terminal-settings'], queryFn: elowenClient.myTerminalSettings, staleTime: 5 * 60 * 1000 });

/** The current user's granular tool permissions (rules + persisted YOLO default). Edited in Account →
 *  Elowen AI (the YOLO toggle) and grown by the approval prompt's "Always allow" flow. */
export const useMyPermissions = () =>
  useQuery({ queryKey: ['my-permissions'], queryFn: elowenClient.myPermissions });

/** The current user's navigation layout (hidden worlds + their order). Read by the shell on every render,
 *  so it is cached like the rest of the chrome and refreshed only when the user edits the menu. */
export const useMyNavSettings = () =>
  useQuery({ queryKey: ['my-nav-settings'], queryFn: elowenClient.myNavSettings, staleTime: 5 * 60 * 1000 });

/** The signed-in user's bell alerts: open rows plus the unread badge count. Read-marking mutations
 *  write this cache optimistically and invalidate it, so the badge and the list cannot disagree. */
export const useAlerts = () =>
  useQuery({ queryKey: QUERY_KEYS.alerts, queryFn: elowenClient.alerts });

/** Account-wide kind preferences, shared by the bell and push delivery. */
export const useMyAlertSettings = () =>
  useQuery({ queryKey: ['my-alert-settings'], queryFn: elowenClient.myAlertSettings, staleTime: 5 * 60 * 1000 });

/** Localized, server-authorized catalog; consumers never filter it again. */
export const useMyAlertKinds = () => {
  const { locale } = useTranslation();
  return useQuery({ queryKey: ['my-alert-kinds', locale], queryFn: elowenClient.myAlertKinds });
};

/** ADMIN: the instance-wide account defaults a human account inherits while it holds no value of its
 *  own. The route is admin-only, so only the admin surfaces that mount it request it. */
export const useAccountPresets = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.accountPresets, queryFn: elowenClient.accountPresets, enabled });

export const useUsersDefaults = () =>
  useQuery({ queryKey: QUERY_KEYS.usersDefaults, queryFn: elowenClient.usersDefaults });

/** ADMIN: every plugin's default per-account form (schema without secrets, values, revision and the
 *  number of accounts holding their own value). Read only while the defaults window is open. */
export const useUsersDefaultsPlugins = (enabled = true) =>
  useQuery({ queryKey: QUERY_KEYS.usersDefaultsPlugins, queryFn: elowenClient.usersDefaultsPlugins, enabled });

/** The caller's own per-plugin settings, with schema and masked values. */
export const useUserPluginConfigs = () =>
  useQuery({ queryKey: QUERY_KEYS.userPluginConfigs, queryFn: elowenClient.userPluginConfigs, staleTime: 60_000 });

/** Installed daemon plugins (admin). Toggling invalidates ['plugins']. */
export const usePlugins = () =>
  useQuery({ queryKey: ['plugins'], queryFn: elowenClient.plugins, staleTime: 60_000 });

/** One plugin's settings detail (schema + values, secrets masked). */
export const usePluginDetail = (name: string | null) =>
  useQuery({ queryKey: ['plugin', name], queryFn: () => elowenClient.pluginDetail(name as string), enabled: !!name });

/** Runtime contributions owned by one plugin (tools/skills/platforms/hooks/…). Powers Tools + Hooks detail. */
export const usePluginContributions = (name: string | null) =>
  useQuery({ queryKey: ['plugin-contributions', name], queryFn: () => elowenClient.pluginContributions(name as string), enabled: !!name });

/** The contributions of the WHOLE loaded registry, unfiltered — read by a control that must ask whether any
 *  plugin provides something before offering a knob that depends on it. `enabled` is passed by the caller so
 *  the request happens when that control can actually use the answer, not on every visit to the page.
 *
 *  `gcTime: 0` is what makes "uncached" true rather than aspirational. Toggling the plugin that provides the
 *  contribution IS how the control behind it is switched off, and the default cache would hand a re-opened
 *  control the PREVIOUS answer to paint with while the refetch is still in flight — briefly offering a knob
 *  that no longer does anything. Dropping the entry the moment the control closes means the next open starts
 *  from `undefined`, which every caller already reads as "not known yet, withhold the control". */
export const useRuntimeContributions = (enabled = true) =>
  useQuery({ queryKey: ['plugin-runtime-contributions'], queryFn: elowenClient.runtimeContributions, enabled, gcTime: 0 });

/** The tail of one plugin's log ring buffer plus derived health (the Logs detail section). Polled every
 *  3 s while a detail is open so logs + the health badge stay live without a manual refresh; the query is
 *  disabled (and polling stops) as soon as the detail closes. */
export const usePluginLogs = (name: string | null) =>
  useQuery({ queryKey: ['plugin-logs', name], queryFn: () => elowenClient.pluginLogs(name as string), enabled: !!name, refetchInterval: name ? 3000 : false });

/** How often the open log viewer re-pulls the file list and the selected file's tail, so new entries
 *  appear without a manual refresh. A few seconds keeps it live without hammering the daemon; polling
 *  only runs while the modal (and thus these observers) is mounted with `poll` set. */
const LOG_POLL_MS = 3000;

/** The daily log files on disk (Settings → Data → Logs). `poll` is set only by the open log viewer so
 *  a new day's file / changing sizes stay current; the Data-tab summary reads it without polling, and a
 *  day roll-over or a delete already invalidates it. */
export const useLogFiles = (enabled = true, poll = false) =>
  useQuery({ queryKey: ['log-files'], queryFn: () => elowenClient.logFiles(), enabled, refetchInterval: poll ? LOG_POLL_MS : false });

/** A bounded tail of one log file. `lines` is part of the key so asking for the whole file after a
 *  truncated read fetches instead of serving the short cached copy. `poll` keeps the tail live in the
 *  open viewer — it is left off for a full-file read so polling never re-pulls a large payload. */
export const useLogFile = (name: string | null, lines?: number, poll = false) =>
  useQuery({
    queryKey: ['log-file', name, lines ?? null],
    queryFn: () => elowenClient.logFile(name as string, lines),
    enabled: !!name,
    // Stop the poll once the read has errored: a file deleted from under the viewer (404) would otherwise
    // 404 every few seconds forever. The healthy tail keeps its interval; the error state's manual retry
    // clears the error and resumes polling.
    refetchInterval: poll ? (q) => (q.state.status === 'error' ? false : LOG_POLL_MS) : false,
  });

/** One plugin's hook-run audit, newest-first (the Hooks section's recent-executions panel). */
export const usePluginHookExecutions = (name: string | null) =>
  useQuery({ queryKey: ['plugin-hook-executions', name], queryFn: () => elowenClient.pluginHookExecutions(name as string), enabled: !!name });

/** The plugin marketplace catalog — the curated registry cross-referenced with what's on disk (admin). */
export const useMarketplace = () =>
  useQuery({ queryKey: ['marketplace'], queryFn: () => elowenClient.marketplace() });

/** Invalidate the host-owned conversation-link projections after a plugin-owned change. The shared prefix
 *  covers both 'mine' and 'all' scopes. With no mounted query this only marks cached data stale. */
export const useInvalidateConversationLinks = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
};

/** Refetch the /plugins/ui listing the main navigation is built from, so a count a plugin's
 *  `registerSurfaceFact('navBadge', fn)` probe reports moves right after the plugin's own change instead of at the next
 *  focus or reload. With the listing not mounted this only marks it stale. */
export const useInvalidatePluginUi = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pluginUi });
};

/** The subagent plugin's typed sub-agents — built-in + user (admin, the subagent plugin detail). */
export const usePluginSubagents = () =>
  useQuery({ queryKey: ['plugin-subagents'], queryFn: elowenClient.pluginSubagents });

/** Admin-selectable proactive-notification targets across every enabled platform plugin. */
export const useNotificationDestinations = () =>
  useQuery({ queryKey: ['notification-destinations'], queryFn: elowenClient.notificationDestinations, staleTime: 60_000 });

/** Admin-selectable tools from the live built-in + enabled-plugin registry. */
export const usePluginTools = () =>
  useQuery({ queryKey: ['plugin-tools'], queryFn: elowenClient.pluginTools, staleTime: 60_000 });

/** Admin request diagnostics. List queries carry metadata only; segment and raw payloads are separate lazy reads. */
export const useBrainDebugSessions = (filters: Record<string, string | number | undefined>, enabled = true) =>
  useInfiniteQuery({
    queryKey: [...QUERY_KEYS.brainDebugSessions, filters],
    queryFn: ({ pageParam }) => elowenClient.brainDebugSessions({ ...filters, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });

export const useBrainDebugRequests = (sessionId: string | null) =>
  useInfiniteQuery({
    queryKey: [...QUERY_KEYS.brainDebugRequests, sessionId],
    queryFn: ({ pageParam }) => elowenClient.brainDebugRequests(sessionId as string, { cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!sessionId,
  });

export const useBrainDebugRequest = (sessionId: string | null, requestId: string | null) =>
  useQuery({
    queryKey: [...QUERY_KEYS.brainDebugRequest, sessionId, requestId],
    queryFn: () => elowenClient.brainDebugRequest(sessionId as string, requestId as string),
    enabled: !!sessionId && !!requestId,
  });

export const useBrainDebugSegment = (sessionId: string | null, requestId: string | null, index: number | null) =>
  useQuery({
    queryKey: ['brain-debug-segment', sessionId, requestId, index],
    queryFn: () => elowenClient.brainDebugSegment(sessionId as string, requestId as string, index as number),
    enabled: !!sessionId && !!requestId && index !== null,
  });

export const useBrainDebugRaw = (sessionId: string | null, requestId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['brain-debug-raw', sessionId, requestId],
    queryFn: () => elowenClient.brainDebugRaw(sessionId as string, requestId as string),
    enabled: enabled && !!sessionId && !!requestId,
  });

export const useBrainDebugLegacy = (sessionId: string | null, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ['brain-debug-legacy', sessionId],
    queryFn: ({ pageParam }) => elowenClient.brainDebugLegacy(sessionId as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: enabled && !!sessionId,
  });

/** The caller's brain conversations (web chat session picker). */
export const useBrainSessions = () =>
  useQuery({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all') });

/** Active projections share the all-conversation cache with exact-id consumers. */
export const activeConversations = <T extends { archivedAt: string | null }>(rows: readonly T[]): T[] =>
  rows.filter(row => row.archivedAt === null);

/** One conversation's members (owner and guests), read while the members dialog is mounted. Keyed under
 *  the conversation list, so every `['brain-sessions']` invalidation (the `conversations` SSE channel, an
 *  invite, removal or leave) also refreshes it. */
export const useParticipants = (sessionId: string) =>
  useQuery({
    queryKey: ['brain-sessions', sessionId, 'participants'],
    queryFn: () => elowenClient.brainParticipants(sessionId),
  });

/** Owner only: who may still be invited to a conversation. Same parent key as the members, for the same
 *  reason: an accepted invite removes the account from this list. */
export const useInvitable = (sessionId: string | null, enabled = true) =>
  useQuery({
    queryKey: ['brain-sessions', sessionId, 'invitable'],
    queryFn: () => elowenClient.brainInvitable(sessionId as string),
    enabled: !!sessionId && enabled,
  });

/** Pickable brain models across all configured providers (the Account → CLI dropdown source). */
export const useBrainModels = () =>
  useQuery({ queryKey: ['brain-models'], queryFn: elowenClient.brainModels, staleTime: 60_000 });

/** Full configured catalog for an administrator editing account grants, not their own picker. */
export const useGrantableBrainModels = () =>
  useQuery({ queryKey: ['grantable-brain-models'], queryFn: elowenClient.grantableBrainModels, staleTime: 60_000 });

/** The exact-id common models of a draft or stored router's member accounts (`POST /brain/providers/router/models`),
 *  read only while the membership is a valid router (`enabled`). Keyed by the member ids; the daemon, not the
 *  browser, computes the intersection. */
export const useBrainRouterModels = (connectionIds: readonly string[], enabled: boolean) =>
  useQuery({
    queryKey: ['brain-router-models', connectionIds],
    queryFn: () => elowenClient.brainRouterModels({ connectionIds: [...connectionIds] }),
    enabled,
    staleTime: 60_000,
  });

/** The provider KIND catalog Settings → Brain offers under its one "add" action (admin). Static per daemon
 *  build: it names the sign-in services, the pasted-key services and the custom endpoint types, never an
 *  account, so it is read once and never invalidated by connecting one. */
export const useProviderKinds = () =>
  useQuery({ queryKey: ['brain-provider-kinds'], queryFn: elowenClient.brainProviderKinds, staleTime: Infinity });

/** Which brain OAuth accounts are connected (admin, Settings → Brain). */
export const useBrainOauthStatus = () =>
  useQuery({ queryKey: ['brain-oauth'], queryFn: elowenClient.brainOauthStatus });

/** Usage per connected provider account (Settings → Brain and the chat's Limits section). The daemon reads
 *  every account on its own schedule and announces each change as a `provider-usage` event, on which
 *  `useElowenEvents` invalidates this key, as it does whenever its stream (re)connects; so nothing here
 *  polls. A mount re-reads, since an unmounted view missed nothing it would otherwise keep. */
export const useBrainRateLimitsAll = () =>
  useQuery({
    queryKey: QUERY_KEYS.brainRateLimits,
    queryFn: elowenClient.brainRateLimitsAll,
    refetchOnMount: true,
    staleTime: 0,
  });

/** What is filling the chat's context window, for the Usage modal's Context section. Fetched only while
 *  that section is on screen (`enabled`) — it walks the live transcript, so there is no reason to poll it
 *  from a closed modal. Keyed by conversation so switching chats cannot show the previous one's figures. */
export const useBrainContextUsage = (session: string | null, enabled = true) =>
  useQuery({
    queryKey: [...QUERY_KEYS.brainContextUsage, session],
    queryFn: () => elowenClient.brainContextUsage(session ?? undefined),
    enabled,
    refetchInterval: 15_000,
  });

/** The chat's own status row, for the `/reasoning` picker: which effort levels the CURRENT model offers,
 *  their labels and the one in force. Fetched only while that modal is open — the controller's own status
 *  poll is about usage and does not carry them into React state. Keyed by conversation. */
export const useBrainSessionStatus = (session: string | null, enabled = true) =>
  useQuery({
    queryKey: ['brain-session-status', session],
    queryFn: () => elowenClient.brainStatus(session ?? undefined),
    enabled,
    staleTime: 0,
  });

export const useUserProjects = (userId: number | null, enabled = true) =>
  useQuery({ queryKey: ['user-projects', userId], queryFn: () => elowenClient.userProjects(userId as number), enabled: !!userId && enabled });

/** Admin: the effective tool access + per-user overview stats for the users-panel detail. Keyed by
 *  user id and only fetched when a user is selected (and the viewer is an admin). */
export const useUserTools = (userId: number | null, enabled = true) =>
  useQuery({ queryKey: ['user-tools', userId], queryFn: () => elowenClient.userTools(userId as number), enabled: !!userId && enabled });

export const useUserStats = (userId: number | null, enabled = true) =>
  useQuery({ queryKey: ['user-stats', userId], queryFn: () => elowenClient.userStats(userId as number), enabled: !!userId && enabled });

/** The caller's own memories, filtered by status/kind/search. Private per-user — identity is server-side.
 *  Mutations invalidate the ['memories'] prefix, so every filter view refreshes together. */
export const useMemories = (filters?: MemoryFilters) =>
  useQuery({ queryKey: ['memories', filters ?? {}], queryFn: () => elowenClient.memories(filters) });

/** One memory (any status) for the detail pane. Disabled until an id is selected. */
export const useMemory = (id: number | null) =>
  useQuery({ queryKey: ['memory', id], queryFn: () => elowenClient.memory(id as number), enabled: id != null });

/** A memory's audit trail (`id` set) or the whole-user event feed (`id` null). Always enabled — a null
 *  id is the valid "everything" feed, not an unselected state. */
export const useMemoryEvents = (id: number | null) =>
  useQuery({ queryKey: ['memory-events', id], queryFn: () => elowenClient.memoryEvents(id ?? undefined) });

/** One memory's vitality curve for the detail drawer. Disabled until a memory is selected, like
 *  {@link useMemory}. */
export const useMemoryVitalityHistory = (id: number | null) =>
  useQuery({
    queryKey: ['memory-vitality', id],
    queryFn: () => elowenClient.memoryVitalityHistory(id as number),
    enabled: id != null,
  });

/** Workspace embedding provider settings (Memory → embedding section). Mutations invalidate this key. */
export const useEmbeddingSettings = () =>
  useQuery({ queryKey: QUERY_KEYS.embeddingSettings, queryFn: elowenClient.embeddingSettings });

/** The caller's own memory categories. Category/reclassify mutations invalidate this key. */
export const useMemoryCategories = () =>
  useQuery({ queryKey: QUERY_KEYS.memoryCategories, queryFn: elowenClient.memoryCategories });

/** Workspace categorization provider settings (Memory → categorization section). Mutations invalidate this key. */
export const useCategorizationSettings = () =>
  useQuery({ queryKey: QUERY_KEYS.categorizationSettings, queryFn: elowenClient.categorizationSettings });

/** The caller's two background memory-maintenance slots. Poll only while work is actually running. */
export const useMemoryMaintenance = (enabled = true) =>
  useQuery({
    queryKey: QUERY_KEYS.memoryMaintenance,
    enabled,
    queryFn: elowenClient.memoryMaintenance,
    refetchInterval: (query) => {
      const state = query.state.data;
      return state?.reindex?.status === 'running' || state?.recategorize?.status === 'running' ? 1000 : false;
    },
  });


/** One page of the caller's automatic memory-maintenance passes, newest first. `before` is the run-id
 *  cursor the previous page ended on, or null for the first page. There is no poll: the finish of a pass
 *  is pushed as the `memory` SSE event, which invalidates this key from `useElowenEvents`. */
const MEMORY_DREAMING_PAGE_SIZE = 25;
export const useMemoryDreamingStatus = (before: string | null) =>
  useQuery({
    queryKey: [...QUERY_KEYS.memoryDreaming, before],
    queryFn: () => elowenClient.memoryDreaming({ limit: MEMORY_DREAMING_PAGE_SIZE, ...(before ? { before } : {}) }),
  });

/** What one pass changed. Read only while its row is unfolded: a page of runs would otherwise fetch the
 *  before/after of every run a reader never opened. */
export const useMemoryDreamingRun = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: [...QUERY_KEYS.memoryDreaming, 'run', id],
    queryFn: () => elowenClient.memoryDreamingRun(id),
    enabled,
  });

/** The account's own memory-maintenance preference, with the revision `useSaveMyMemoryDreaming` checks a
 *  save against. */
export const useMyMemoryDreaming = () =>
  useQuery({ queryKey: QUERY_KEYS.myMemoryDreaming, queryFn: elowenClient.myMemoryDreaming });

/** The admin view of the instance's skins, for the Settings rows and the add/remove surface. */
export const useSkinAdmin = (enabled = true) =>
  useQuery<SkinAdminView>({
    queryKey: QUERY_KEYS.skinAdmin,
    queryFn: () => elowenClient.skinAdmin(),
    enabled,
  });

/** The instance's skin catalogue, as the document resolves it: which designs exist, what each one is built
 *  on and where a customer skin's stylesheet is served from.
 *
 *  `seed` is what the server rendered the document with (`fetchSkinCatalogue`), and it is `initialData`
 *  rather than a fallback: the first client render must agree with the server's HTML byte for byte, or
 *  every page load would hydrate in the wrong design. Everything that resolves a skin — the document's
 *  attributes, the switcher, the Settings allow-list — reads this one value, so it is the skin mutations
 *  that invalidate it: with only the built-ins compiled in, a skin added in Settings was unpickable and a
 *  removed one could stay worn until the next page load.
 *
 *  A payload the boundary parser refuses throws rather than degrading to the built-ins, which is the same
 *  rule the server-side fetch applies — the last good catalogue then stays in the cache. */
export const useSkinCatalogue = (seed: SkinCatalogue): SkinCatalogue => {
  const query = useQuery<SkinCatalogue>({
    queryKey: QUERY_KEYS.skinCatalogue,
    queryFn: async () => {
      const catalogue = parseSkinCatalogue(await elowenClient.skinCatalogue());
      if (!catalogue) throw new ElowenApiError('malformed skin catalogue', 502);
      return catalogue;
    },
    initialData: seed,
  });
  // `initialData` guarantees data, and react-query keeps the last good value through a failed refetch; the
  // seed is only what the TYPE cannot know it has.
  return query.data ?? seed;
};
